import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  autoUpdate,
  flip,
  shift,
  offset,
} from "@floating-ui/dom";
import { useTransition } from "stimulus-use";

const TRANSITION_CONFIG = {
  enterActive: "tw-transition-opacity tw-duration-200 tw-ease-out",
  enterFrom: "tw-opacity-0",
  enterTo: "tw-opacity-100",
  leaveActive: "tw-transition-opacity tw-duration-150 tw-ease-in",
  leaveFrom: "tw-opacity-100",
  leaveTo: "tw-opacity-0",
  hiddenClass: "tw-hidden",
};

const POSITION_CONFIG = {
  placement: "bottom-start",
  middleware: [offset(8), flip(), shift({ padding: 8 })],
};

const LOADER_HTML = `
  <div class="tw-flex tw-justify-center tw-items-center tw-p-4">
    <div class="tw-animate-spin tw-h-5 tw-w-5 tw-border-2 tw-border-gray-300 tw-border-t-blue-600 tw-rounded-full"></div>
  </div>
`;

export default class UIPopover extends Controller {
  static values = {
    dismissAfter: Number,
    preventDefault: Boolean,
  };

  static targets = ["content", "trigger"];

  connect() {
    this.#initializeTransition();
    this.#setupEventListeners();
    this.#setupPositioning();
  }

  disconnect() {
    this.#cleanupEventListeners();
  }

  toggle(event) {
    this.#handlePreventDefault(event);
    this.toggleTransition();

    if (this.#isPopoverVisible()) {
      this.#handleVisiblePopover();
    }
  }

  async updatePosition() {
    const { x, y } = await computePosition(
      this.triggerTarget,
      this.contentTarget,
      POSITION_CONFIG
    );

    this.#applyPosition(x, y);
  }

  clickOutside(event) {
    if (!this.#isClickedInside(event)) {
      this.leave();
    }
  }

  // Private methods
  #initializeTransition() {
    useTransition(this, {
      element: this.contentTarget,
      ...TRANSITION_CONFIG,
    });
  }

  #setupEventListeners() {
    this.clickOutsideHandler = this.clickOutside.bind(this);
    document.addEventListener("click", this.clickOutsideHandler);
  }

  #setupPositioning() {
    this.cleanup = autoUpdate(
      this.triggerTarget,
      this.contentTarget,
      this.updatePosition.bind(this)
    );
  }

  #cleanupEventListeners() {
    document.removeEventListener("click", this.clickOutsideHandler);
    if (this.cleanup) this.cleanup();
  }

  #handlePreventDefault(event) {
    if (this.preventDefaultValue) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  #isPopoverVisible() {
    return !this.contentTarget.classList.contains("tw-hidden");
  }

  #handleVisiblePopover() {
    this.updatePosition();
    this.#handleRemoteContent();
    this.#setupAutoDismiss();
  }

  #handleRemoteContent() {
    if (this.#hasRemoteTrigger()) {
      this.contentTarget.innerHTML = LOADER_HTML;
    }
  }

  #hasRemoteTrigger() {
    const triggerEl = this.triggerTarget;
    return (
      triggerEl.hasAttribute("data-remote") ||
      triggerEl.querySelector('[data-remote="true"]')
    );
  }

  #setupAutoDismiss() {
    if (this.hasDismissAfterValue) {
      setTimeout(() => this.leave(), this.dismissAfterValue);
    }
  }

  #applyPosition(x, y) {
    Object.assign(this.contentTarget.style, {
      left: `${x}px`,
      top: `${y}px`,
      position: "absolute",
    });
  }

  #isClickedInside(event) {
    return event.composedPath().includes(this.element);
  }
}
